import React, { useState, useRef, useEffect } from 'react';
import {
  ProgressBar,
  Overlay,
  OverlayTrigger,
  Tooltip,
  Popover,
  Button,
} from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLevelUp } from '@whitebox-learning/fontawesome-pro-light-svg-icons';

import { GameModeIcon } from '../Common/Icons';

import './LevelProgressBar.sass';

export default function LevelProgressBar({
  game,
  showInfo,
  toggleGameModal,
  firstRun = false,
}) {
  const prevPointsRef = useRef(game.aggregateScore);
  const newPointsRef = useRef(0);
  const levelRef = useRef(game.level.level);
  const mainEl = useRef(null);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [showUpdated, setShowUpdated] = useState(false);

  // show level up popover when available
  useEffect(() => {
    if (game.canLevelUp) {
      setShowLevelUp(true);
    }
  }, [game.canLevelUp]);

  // show a tooltip for 3 seconds when some action results in an updated game score
  useEffect(() => {
    if (game.level.level === levelRef.current) {
      newPointsRef.current = game.aggregateScore - prevPointsRef.current;
      if (newPointsRef.current > 0) {
        prevPointsRef.current = game.aggregateScore;
        setShowUpdated(true);
        setTimeout(() => setShowUpdated(false), 3000);
      }
    } else {
      levelRef.current = game.level.level;
    }
  }, [game.aggregateScore]);

  const { aggregateScore, level, canLevelUp } = game;
  const progress = Math.ceil(
    Math.min((aggregateScore / level.requiredAggregateScore) * 100, 100)
  );

  return (
    <div className="game-status p-0 ps-2 pe-3" ref={mainEl}>
      <OverlayTrigger
        key="nationals-info"
        placement="bottom"
        overlay={
          <Tooltip id="nationals-tooltip">
            Click to read about WhiteBox Nationals
          </Tooltip>
        }
      >
        <Button variant="link" onClick={showInfo} className="p-0">
          <GameModeIcon className="p-0 fs-3" />
        </Button>
      </OverlayTrigger>
      <div className="progress-and-level pb-2">
        <span className="fw-normal">Level {level.level}</span>
        <ProgressBar
          now={progress}
          label={progress > 20 ? `${progress}%` : undefined}
          onClick={toggleGameModal}
          variant={canLevelUp ? 'success' : 'default'}
          striped
        />
      </div>
      <Overlay
        placement="bottom"
        show={firstRun && aggregateScore <= 0}
        target={mainEl.current}
      >
        <Popover id="game-mode-intro" title="Game Mode">
          <p>
            Your teacher has enabled game mode! Click the progress bar to learn
            more.
          </p>
        </Popover>
      </Overlay>
      <Overlay
        placement="bottom"
        show={!game.canChangeLevels && canLevelUp && showLevelUp}
        target={mainEl.current}
      >
        <Popover id="game-mode-intro" title="Ready to Level Up!">
          <Popover.Header>
            Level Up!{' '}
            <FontAwesomeIcon icon={faLevelUp} size="lg" className="float-end" />{' '}
          </Popover.Header>
          <Popover.Body>
            <p>You're ready to move to the next level!</p>
            <div className="d-flex justify-content-between">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setShowLevelUp(false)}
              >
                Remind Me Later
              </Button>
              <Button
                size="sm"
                variant="wb"
                onClick={() => {
                  setShowLevelUp(false);
                  toggleGameModal();
                }}
              >
                Let's Go!
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
      <Overlay placement="right" show={showUpdated} target={mainEl.current}>
        <Tooltip id="game-progress-updated-tooltip" animate>
          You earned {newPointsRef.current} points!
        </Tooltip>
      </Overlay>
    </div>
  );
}
